import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

const FloorplanTo3D = ({ floorplanData, isVisible }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [renderStats, setRenderStats] = useState({
    walls: 0,
    rooms: 0,
    vertices: 0,
    faces: 0
  });

  // 3D-Konfiguration
  const config = {
    wallHeight: 3.0,
    wallThickness: 0.2,
    floorThickness: 0.1,
    ceilingHeight: 3.2,
    scale: 0.01, // Canvas-Pixel zu Meter-Umrechnung
    colors: {
      wall: 0xcccccc,
      floor: 0x8B4513,
      ceiling: 0xf5f5f5,
      rooms: {
        living_room: 0x90EE90,
        kitchen: 0xFFB347,
        bedroom: 0xDDA0DD,
        bathroom: 0x87CEEB,
        hallway: 0xF0E68C,
        balcony: 0x98FB98,
        storage: 0xD3D3D3,
        office: 0xFFB6C1,
        unknown: 0xE6E6FA
      }
    }
  };

  useEffect(() => {
    if (!isVisible || !mountRef.current) return;

    initializeScene();
    
    return () => {
      cleanup();
    };
  }, [isVisible]);

  useEffect(() => {
    if (floorplanData && isVisible && sceneRef.current) {
        generate3DModel();
    }
  }, [floorplanData, isVisible]);

  const initializeScene = () => {
    if (!mountRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(
      75,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(10, 15, 10);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // Controls (OrbitControls würde hier importiert werden)
    // Für jetzt verwenden wir einfache Maus-Rotation
    let isMouseDown = false;
    let mouseX = 0;
    let mouseY = 0;

    const onMouseDown = (event) => {
      isMouseDown = true;
      mouseX = event.clientX;
      mouseY = event.clientY;
    };

    const onMouseMove = (event) => {
      if (!isMouseDown) return;
      
      const deltaX = event.clientX - mouseX;
      const deltaY = event.clientY - mouseY;
      
      camera.position.x = camera.position.x * Math.cos(deltaX * 0.01) - camera.position.z * Math.sin(deltaX * 0.01);
      camera.position.z = camera.position.x * Math.sin(deltaX * 0.01) + camera.position.z * Math.cos(deltaX * 0.01);
      camera.position.y += deltaY * 0.05;
      
      camera.lookAt(0, 0, 0);
      
      mouseX = event.clientX;
      mouseY = event.clientY;
    };

    const onMouseUp = () => {
      isMouseDown = false;
    };

    renderer.domElement.addEventListener('mousedown', onMouseDown);
    renderer.domElement.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('mouseup', onMouseUp);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 10, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);

    // Grid Helper
    const gridHelper = new THREE.GridHelper(20, 20, 0x888888, 0xcccccc);
    scene.add(gridHelper);

    mountRef.current.appendChild(renderer.domElement);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();
  };

  const generateWallGeometry = (points) => {
    if (points.length < 2) return null;

    const shape = new THREE.Shape();
    
    // Konvertiere Canvas-Koordinaten zu 3D-Koordinaten
    const scaledPoints = points.map(p => ({
      x: (p.x - 400) * config.scale, // Zentriere um Canvas-Mitte
      z: -(p.y - 300) * config.scale // Invertiere Y-Achse für 3D
    }));

    shape.moveTo(scaledPoints[0].x, scaledPoints[0].z);
    for (let i = 1; i < scaledPoints.length; i++) {
      shape.lineTo(scaledPoints[i].x, scaledPoints[i].z);
    }

    const extrudeSettings = {
      depth: config.wallHeight,
      bevelEnabled: false
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.rotateX(-Math.PI / 2); // Rotiere um X-Achse für vertikale Wände
    
    return geometry;
  };

  const generateFloorGeometry = (walls) => {
    if (!walls || walls.length === 0) return null;

    // Erstelle Boden basierend auf Wand-Umriss
    const allPoints = [];
    walls.forEach(wall => {
      wall.forEach(point => {
        allPoints.push({
          x: (point.x - 400) * config.scale,
          z: -(point.y - 300) * config.scale
        });
      });
    });

    if (allPoints.length === 0) return null;

    // Finde Bounding Box
    const minX = Math.min(...allPoints.map(p => p.x));
    const maxX = Math.max(...allPoints.map(p => p.x));
    const minZ = Math.min(...allPoints.map(p => p.z));
    const maxZ = Math.max(...allPoints.map(p => p.z));

    const width = maxX - minX;
    const height = maxZ - minZ;

    const geometry = new THREE.PlaneGeometry(width + 1, height + 1);
    geometry.rotateX(-Math.PI / 2);
    geometry.translate((minX + maxX) / 2, -config.floorThickness, (minZ + maxZ) / 2);

    return geometry;
  };

  const generateRoomMarkers = (rooms) => {
    const markers = [];
    
    rooms.forEach((room, index) => {
      const geometry = new THREE.CylinderGeometry(0.3, 0.3, 0.1, 8);
      const color = config.colors.rooms[room.type] || config.colors.rooms.unknown;
      const material = new THREE.MeshLambertMaterial({ color });
      
      const marker = new THREE.Mesh(geometry, material);
      marker.position.set(
        (room.x - 400) * config.scale,
        0.05,
        -(room.y - 300) * config.scale
      );
      
      markers.push(marker);

      // Raum-Label
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 128;
      canvas.height = 64;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = '#000000';
      context.font = '16px Arial';
      context.textAlign = 'center';
      context.fillText(`${index + 1}`, canvas.width / 2, canvas.height / 2 + 6);

      const texture = new THREE.CanvasTexture(canvas);
      const labelMaterial = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
      const labelGeometry = new THREE.PlaneGeometry(0.5, 0.25);
      
      const label = new THREE.Mesh(labelGeometry, labelMaterial);
      label.position.set(
        (room.x - 400) * config.scale,
        0.5,
        -(room.y - 300) * config.scale
      );
      label.lookAt(cameraRef.current.position);
      
      markers.push(label);
    });

    return markers;
  };

  const generate3DModel = async () => {
    if (!floorplanData || !sceneRef.current) return;

    setIsLoading(true);

    try {
      // Lösche vorherige Modelle
      const objectsToRemove = [];
      sceneRef.current.traverse((child) => {
        if (child.userData.isFloorplanObject) {
          objectsToRemove.push(child);
        }
      });
      objectsToRemove.forEach(obj => sceneRef.current.remove(obj));

      let totalVertices = 0;
      let totalFaces = 0;

      // Generiere Wände
      if (floorplanData.walls && floorplanData.walls.length > 0) {
        floorplanData.walls.forEach((wall, index) => {
          const geometry = generateWallGeometry(wall);
          if (geometry) {
            const material = new THREE.MeshLambertMaterial({ color: config.colors.wall });
            const wallMesh = new THREE.Mesh(geometry, material);
            wallMesh.userData.isFloorplanObject = true;
            wallMesh.userData.type = 'wall';
            wallMesh.castShadow = true;
            wallMesh.receiveShadow = true;
            sceneRef.current.add(wallMesh);

            totalVertices += geometry.attributes.position.count;
            totalFaces += geometry.index ? geometry.index.count / 3 : geometry.attributes.position.count / 3;
          }
        });
      }

      // Generiere Boden
      const floorGeometry = generateFloorGeometry(floorplanData.walls);
      if (floorGeometry) {
        const floorMaterial = new THREE.MeshLambertMaterial({ color: config.colors.floor });
        const floorMesh = new THREE.Mesh(floorGeometry, floorMaterial);
        floorMesh.userData.isFloorplanObject = true;
        floorMesh.userData.type = 'floor';
        floorMesh.receiveShadow = true;
        sceneRef.current.add(floorMesh);

        totalVertices += floorGeometry.attributes.position.count;
        totalFaces += floorGeometry.index ? floorGeometry.index.count / 3 : floorGeometry.attributes.position.count / 3;
      }

      // Generiere Raum-Marker
      if (floorplanData.rooms && floorplanData.rooms.length > 0) {
        const roomMarkers = generateRoomMarkers(floorplanData.rooms);
        roomMarkers.forEach(marker => {
          marker.userData.isFloorplanObject = true;
          marker.userData.type = 'room_marker';
          sceneRef.current.add(marker);
        });
      }

      // Update Statistiken
      setRenderStats({
        walls: floorplanData.walls ? floorplanData.walls.length : 0,
        rooms: floorplanData.rooms ? floorplanData.rooms.length : 0,
        vertices: Math.round(totalVertices),
        faces: Math.round(totalFaces)
      });

    } catch (error) {
      console.error('Fehler bei 3D-Generierung:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const cleanup = () => {
    if (rendererRef.current && mountRef.current) {
      mountRef.current.removeChild(rendererRef.current.domElement);
      rendererRef.current.dispose();
    }
  };

  const exportGLTF = () => {
    // Hier würde GLTFExporter implementiert werden
    console.log('GLTF Export würde hier implementiert werden');
    alert('GLTF Export-Funktion wird in der nächsten Version verfügbar sein');
  };

  if (!isVisible) return null;

  return (
    <div className="w-full h-full flex flex-col">
      {/* 3D Viewer Header */}
      <div className="bg-white dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              🎯 3D-Modell Vorschau
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Maus ziehen zum Rotieren | Scroll zum Zoomen
            </p>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={generate3DModel}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? '🔄 Generiere...' : '🔄 Neu generieren'}
            </button>
            <button
              onClick={exportGLTF}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              📦 GLTF Export
            </button>
          </div>
        </div>
      </div>

      {/* 3D Viewer */}
      <div className="flex-1 relative">
        <div 
          ref={mountRef} 
          className="w-full h-full"
          style={{ minHeight: '400px' }}
        />
        
        {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span>3D-Modell wird generiert...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3D Stats */}
      <div className="bg-gray-50 dark:bg-gray-900 p-3 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-4 gap-4 text-sm">
          <div className="text-center">
            <div className="font-semibold text-gray-900 dark:text-white">{renderStats.walls}</div>
            <div className="text-gray-600 dark:text-gray-400">Wände</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900 dark:text-white">{renderStats.rooms}</div>
            <div className="text-gray-600 dark:text-gray-400">Räume</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900 dark:text-white">{renderStats.vertices}</div>
            <div className="text-gray-600 dark:text-gray-400">Vertices</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-gray-900 dark:text-white">{renderStats.faces}</div>
            <div className="text-gray-600 dark:text-gray-400">Faces</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorplanTo3D;
